/**
 * A record's activity timeline — composer plus the real §8 list underneath.
 *
 * Originally built for the lead detail page (`lead-detail.html`'s Activity
 * tab) and extracted here once the account detail page needed the identical
 * thing: `useActivities({ [linkField]: recordId })` is `GET
 * /activities?lead_id=…` or `?account_id=…`, whichever the caller is — §8's own
 * filter, not a per-record include on `GET /leads/{id}` / `GET /accounts/{id}`
 * (both still answer 501 for `include=activities`). Composing writes a real row
 * via `POST /activities`; the pencil on a card PATCHes its subject; the check
 * writes `.../complete`; the trash soft-deletes with a reason.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { EmptyState, Modal, Field, Badge } from '../../components/common/index.jsx';
import { ButtonSpinner, SkeletonText } from '../../components/common/Loading.jsx';
import { relativeTime, dateTime, date, time } from '../../utils/formatters.js';
import { useAuth } from '../../hooks/useAuth.js';
import { useActivities, useCreateActivity, useUpdateActivity, useCompleteActivity, useDeleteActivity } from './useActivities.js';

const COMPOSER_KINDS = {
  task: { icon: 'bi-check2-square', label: 'New Task', placeholder: 'Recap your task…', kind: 'task' },
  call: { icon: 'bi-telephone', label: 'Log a Call', placeholder: 'Recap the call…', kind: 'call' },
  event: { icon: 'bi-calendar-event', label: 'New Event', placeholder: 'Describe the meeting…', kind: 'meeting' },
};

/** `crm.activity_kind` → the icon/colour the prototype uses for that kind of card. Shared with CalendarPage.jsx, so an event's colour matches its card everywhere. */
export const KIND_META = {
  call: ['bi-telephone-fill', 'var(--brand-500)'],
  meeting: ['bi-people-fill', 'var(--emerald)'],
  email: ['bi-envelope-open-fill', 'var(--sky)'],
  task: ['bi-check2-square', 'var(--brand-500)'],
  note: ['bi-journal-text', 'var(--ink-3)'],
  whatsapp: ['bi-whatsapp', '#25d366'],
  sms: ['bi-chat-dots-fill', 'var(--sky)'],
  visit: ['bi-geo-alt-fill', 'var(--amber)'],
  demo: ['bi-easel-fill', 'var(--violet)'],
};

/** Kind label/select-option lists shared by every screen that lets someone
 * pick or filter on `crm.activity_kind` — `ActivitiesPage.jsx`'s filter bar
 * and both its Log/Edit modals, so the nine kinds read the same everywhere. */
export const KIND_LABEL = {
  call: 'Call', meeting: 'Meeting', email: 'Email', task: 'Task', note: 'Note',
  whatsapp: 'WhatsApp', sms: 'SMS', visit: 'Visit', demo: 'Demo',
};
export const KIND_OPTIONS = Object.entries(KIND_LABEL).map(([value, label]) => ({ value, label }));
export const PRIORITY_OPTIONS = ['Low', 'Normal', 'High', 'Urgent'];

/** ISO string → the local `YYYY-MM-DDTHH:mm` a `datetime-local` input needs.
 * Built from the local field getters, not a UTC slice, so the value shown
 * matches the visitor's own clock rather than shifting by their timezone. */
export function toLocalInputValue(iso) {
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Whether an activity's scheduled time (if any) has already arrived —
 * `CalendarPage.jsx` only allows completing an event once it's actually
 * happened, not one still ahead on the calendar. An activity with no
 * `scheduled_start`/`due_at` at all (a plain log entry) has nothing to wait
 * for, so it counts as due. */
export function isDueOrPast(activity) {
  const when = activity.scheduled_start ?? activity.due_at;
  return !when || new Date(when) <= new Date();
}

function activityMeta(a) {
  if (a.state === 'completed') return `Completed ${relativeTime(a.completed_at ?? a.updated_at)}`;
  if (a.due_at) return `Due ${dateTime(a.due_at)}`;
  if (a.scheduled_start) return dateTime(a.scheduled_start);
  return `Logged ${relativeTime(a.created_at)}`;
}

/**
 * An activity's `related` object → the record(s) it links to, as
 * `{text, to}` pairs (`to` is `null` where no detail route exists yet).
 * Shared by `ActivityDetail` below and `ActivitiesPage.jsx`'s list column, so
 * "what this activity is about" reads identically everywhere. Does not cover
 * `quotation_id`/`campaign_id` — `related` carries only bare ids for those,
 * no display object, because §7's quotations module and campaigns are not
 * built.
 */
export function relatedRecordLinks(related) {
  if (!related) return [];
  const map = {
    lead: related.lead && { text: related.lead.full_name ?? related.lead.lead_no, to: `/leads/${related.lead.id}` },
    account: related.account && { text: related.account.name ?? related.account.account_no, to: `/accounts/${related.account.id}` },
    contact: related.contact && { text: related.contact.full_name ?? related.contact.contact_no, to: null },
    opportunity: related.opportunity && { text: related.opportunity.name ?? related.opportunity.opportunity_no, to: null },
  };
  return Object.values(map).filter(Boolean);
}

/**
 * Full detail for one activity — subject, kind, when, location/meeting link,
 * owner, what it's related to, notes, and Edit/Mark-complete/Delete actions.
 * Used both as `CalendarPage.jsx`'s event drawer and `ActivitiesPage.jsx`'s
 * row drawer, so an activity looks the same wherever it's opened from.
 * Edit and Mark-complete both disappear once an activity is completed or
 * cancelled — there is nothing left to change or finish. `completeDisabledReason`,
 * when set, keeps Mark complete visible but greys it out with that text as a
 * tooltip instead of hiding it — `CalendarPage.jsx` uses it to block
 * completing an event that hasn't happened yet, while still showing Edit so
 * a not-yet-due event can be rescheduled or otherwise updated.
 */
export function ActivityDetail({
  activity, canComplete, canDelete, canEdit, busy, onComplete, onDelete, onEdit, completeDisabledReason,
}) {
  const isOver = activity.state === 'completed' || activity.state === 'cancelled';
  const [icon, color] = KIND_META[activity.kind] ?? ['bi-circle', 'var(--ink-3)'];
  const related = relatedRecordLinks(activity.related);

  return (
    <div>
      <div className="d-flex align-items-center gap-2 mb-3">
        <span
          className="d-inline-flex align-items-center justify-content-center rounded-circle"
          style={{ width: 32, height: 32, background: `color-mix(in srgb, ${color} 16%, #fff)` }}
        >
          <i className={`bi ${icon}`} style={{ color }} aria-hidden="true" />
        </span>
        <Badge tone={activity.state === 'completed' ? 'emerald' : activity.is_overdue ? 'rose' : 'neutral'}>
          {activity.state === 'completed' ? 'Completed' : activity.is_overdue ? 'Overdue' : activity.state}
        </Badge>
        {activity.priority && activity.priority !== 'Normal' && (
          <Badge tone={activity.priority === 'Urgent' ? 'rose' : 'amber'}>{activity.priority}</Badge>
        )}
      </div>

      <dl className="row g-2 t-sm mb-0">
        {(activity.scheduled_start || activity.due_at) && (
          <>
            <dt className="col-4 text-muted">When</dt>
            <dd className="col-8 mb-0">
              {activity.scheduled_start
                ? (activity.all_day
                  ? date(activity.scheduled_start)
                  : `${dateTime(activity.scheduled_start)}${activity.scheduled_end ? ` – ${time(activity.scheduled_end)}` : ''}`)
                : `Due ${dateTime(activity.due_at)}`}
            </dd>
          </>
        )}
        {activity.location && (
          <>
            <dt className="col-4 text-muted">Location</dt>
            <dd className="col-8 mb-0">{activity.location}</dd>
          </>
        )}
        {activity.meeting_url && (
          <>
            <dt className="col-4 text-muted">Meeting</dt>
            <dd className="col-8 mb-0"><a href={activity.meeting_url} target="_blank" rel="noreferrer">Join link</a></dd>
          </>
        )}
        {activity.owner && (
          <>
            <dt className="col-4 text-muted">Owner</dt>
            <dd className="col-8 mb-0">{activity.owner.full_name}</dd>
          </>
        )}
        {related.length > 0 && (
          <>
            <dt className="col-4 text-muted">Related to</dt>
            <dd className="col-8 mb-0">
              {related.map((r, i) => (
                <span key={i}>
                  {i > 0 && ', '}
                  {r.to ? <Link to={r.to}>{r.text}</Link> : r.text}
                </span>
              ))}
            </dd>
          </>
        )}
        {activity.body && (
          <>
            <dt className="col-4 text-muted">Notes</dt>
            <dd className="col-8 mb-0" style={{ whiteSpace: 'pre-wrap' }}>{activity.body}</dd>
          </>
        )}
        {activity.outcome_note && (
          <>
            <dt className="col-4 text-muted">Outcome</dt>
            <dd className="col-8 mb-0" style={{ whiteSpace: 'pre-wrap' }}>{activity.outcome_note}</dd>
          </>
        )}
      </dl>

      <div className="d-flex gap-2 mt-3">
        {canEdit && onEdit && !isOver && (
          <button type="button" className="btn-x btn-ghost" onClick={onEdit} disabled={busy}>
            Edit
          </button>
        )}
        {canComplete && !isOver && (
          <button
            type="button"
            className={`btn-x ${completeDisabledReason ? 'btn-ghost' : 'btn-primary-x'}`}
            onClick={onComplete}
            disabled={busy || Boolean(completeDisabledReason)}
            title={completeDisabledReason}
          >
            {busy ? 'Marking done…' : 'Mark complete'}
          </button>
        )}
        {canDelete && onDelete && (
          <button type="button" className="btn btn-delete btn-sm-x" onClick={onDelete} disabled={busy}>
            Delete
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * @param {{linkField:'lead_id'|'account_id'|'opportunity_id'|'quotation_id'|'campaign_id',
 *          recordId:string}} props `linkField` is one of `crm.activity`'s five
 *   nullable link columns — whichever the host record is.
 */
export function ActivityTab({ linkField, recordId }) {
  const { can } = useAuth();
  const canLog = can('activities', 'create');
  const canEditActivity = can('activities', 'edit');
  const canDeleteActivity = can('activities', 'delete');

  const [composerKind, setComposerKind] = useState('task');
  const [text, setText] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);

  const { data, isLoading } = useActivities({ [linkField]: recordId, sort: '-created_at', limit: 50 });
  const create = useCreateActivity();
  const complete = useCompleteActivity();
  const update = useUpdateActivity();
  const remove = useDeleteActivity();

  const activities = data?.data ?? [];
  const open = activities.filter((a) => a.state !== 'completed' && a.state !== 'cancelled');
  const done = activities.filter((a) => a.state === 'completed' || a.state === 'cancelled');

  const save = async () => {
    if (!text.trim()) return;
    const c = COMPOSER_KINDS[composerKind];
    const payload = { [linkField]: recordId, kind: c.kind, subject: text.trim() };
    /* Logging a call is recording something that already happened, not
       scheduling something to do — so it is created already completed. */
    if (c.kind === 'call') {
      payload.state = 'completed';
      payload.completed_at = new Date().toISOString();
    }
    await create.mutateAsync(payload);
    setText('');
  };

  return (
    <>
      <div className="composer mb-3">
        <div className="composer-tabs">
          {Object.entries(COMPOSER_KINDS).map(([key, c]) => (
            <button key={key} type="button" className={composerKind === key ? 'active' : ''} onClick={() => setComposerKind(key)}>
              <i className={`bi ${c.icon} me-1`} aria-hidden="true" />{c.label}
            </button>
          ))}
        </div>
        <div className="composer-body">
          <textarea
            className="form-control mb-2" rows={2} placeholder={COMPOSER_KINDS[composerKind].placeholder}
            value={text} onChange={(e) => setText(e.target.value)}
            disabled={!canLog}
          />
          <div className="d-flex align-items-center gap-2">
            {!canLog && <span className="t-2xs ink-4">Needs activities:create.</span>}
            <button type="button" className="btn btn-save btn-sm-x ms-auto" onClick={save} disabled={!canLog || !text.trim() || create.isPending}>
              {create.isPending ? <><ButtonSpinner />Saving…</> : 'Save'}
            </button>
          </div>
        </div>
      </div>

      {isLoading && <SkeletonText lines={3} />}

      {!isLoading && (
        <>
          {open.length > 0 && (
            <div>
              <div className="act-group-head">Open</div>
              {open.map((a) => (
                <ActivityCard
                  key={a.id} a={a}
                  canEdit={canEditActivity} canDelete={canDeleteActivity}
                  completing={complete.isPending}
                  onComplete={() => complete.mutateAsync({ id: a.id, rowVersion: a.row_version })}
                  onRename={(subject) => update.mutateAsync({ id: a.id, patch: { subject }, rowVersion: a.row_version })}
                  onDelete={() => setDeleteTarget(a)}
                />
              ))}
            </div>
          )}
          {done.length > 0 && (
            <div>
              <div className="act-group-head">Completed</div>
              {done.map((a) => (
                <ActivityCard
                  key={a.id} a={a}
                  canEdit={canEditActivity} canDelete={canDeleteActivity}
                  onRename={(subject) => update.mutateAsync({ id: a.id, patch: { subject }, rowVersion: a.row_version })}
                  onDelete={() => setDeleteTarget(a)}
                />
              ))}
            </div>
          )}
          {activities.length === 0 && (
            <EmptyState icon="bi-list-check" title="Nothing logged yet" message="Tasks, calls and events you log here appear as a timeline." />
          )}
        </>
      )}

      <DeleteActivityModal
        activity={deleteTarget}
        busy={remove.isPending}
        onClose={() => setDeleteTarget(null)}
        onConfirm={async (reason) => {
          await remove.mutateAsync({ id: deleteTarget.id, reason, rowVersion: deleteTarget.row_version });
          setDeleteTarget(null);
        }}
      />
    </>
  );
}

function ActivityCard({ a, canEdit, canDelete, completing, onComplete, onRename, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(a.subject);
  const [icon, color] = KIND_META[a.kind] ?? ['bi-circle', 'var(--ink-3)'];
  const open = a.state !== 'completed' && a.state !== 'cancelled';

  const commit = () => {
    setEditing(false);
    const next = value.trim();
    if (next && next !== a.subject) onRename(next);
    else setValue(a.subject);
  };

  return (
    <div className="act-card">
      <div className="d-flex gap-2">
        <div className="ac-ico" style={{ background: color }}><i className={`bi ${icon}`} aria-hidden="true" /></div>
        <div className="min-w-0 flex-grow-1">
          {editing ? (
            <input
              className="ie-input" autoFocus value={value}
              onChange={(e) => setValue(e.target.value)}
              onBlur={commit}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); }
                if (e.key === 'Escape') { setValue(a.subject); setEditing(false); }
              }}
            />
          ) : (
            <span className="ie">
              <span className="ie-val t-sm fw-650">{a.subject}</span>
              {canEdit && (
                <button type="button" className="ie-pencil" title="Rename" onClick={() => setEditing(true)}>
                  <i className="bi bi-pencil-fill" aria-hidden="true" />
                </button>
              )}
            </span>
          )}
          <div className="t-xs ink-3">{activityMeta(a)}</div>
          <div className="t-2xs ink-4 mt-1">
            <i className="bi bi-person me-1" aria-hidden="true" />{a.assigned_to?.full_name ?? a.owner?.full_name ?? 'Unassigned'}
          </div>
        </div>
        <div className="d-flex flex-column align-items-end gap-1">
          {a.state === 'completed'
            ? <span className="pill pill-green no-wrap"><i className="bi bi-check-lg" aria-hidden="true" />Done</span>
            : a.state === 'cancelled'
              ? <span className="pill pill-gray no-wrap">Cancelled</span>
              : a.is_overdue
                ? <span className="pill pill-rose no-wrap">Overdue</span>
                : <span className="pill pill-amber no-wrap">Open</span>}
          <div className="d-flex gap-1">
            {open && (
              <button type="button" className="icon-btn" style={{ width: 24, height: 24, fontSize: 11 }}
                      title="Mark done" disabled={completing} onClick={onComplete}>
                <i className="bi bi-check-lg" aria-hidden="true" />
              </button>
            )}
            {canDelete && (
              <button type="button" className="icon-btn" style={{ width: 24, height: 24, fontSize: 11 }}
                      title="Delete" onClick={onDelete}>
                <i className="bi bi-trash" aria-hidden="true" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Delete needs a reason — `iam.app_operation.requires_reason` for activities
 * too. Exported: `ActivitiesPage.jsx` reuses it rather than a second copy.
 */
export function DeleteActivityModal({ activity, busy, onClose, onConfirm }) {
  const [reason, setReason] = useState('');

  useEffect(() => { if (activity) setReason(''); }, [activity]);

  return (
    <Modal
      open={Boolean(activity)}
      onClose={onClose}
      title="Delete activity"
      footer={(
        <>
          <button type="button" className="btn btn-cancel btn-sm-x" onClick={onClose}>Cancel</button>
          <button
            type="button" className="btn btn-delete btn-sm-x"
            disabled={!reason.trim() || busy} onClick={() => onConfirm(reason.trim())}
          >
            {busy ? <><ButtonSpinner />Deleting…</> : 'Delete'}
          </button>
        </>
      )}
    >
      {activity && (
        <>
          <p className="t-sm ink-2">Deleting <strong>{activity.subject}</strong>. This is a soft delete.</p>
          <Field label="Reason" required>
            <input className="form-control" value={reason} onChange={(e) => setReason(e.target.value)} autoFocus placeholder="e.g. Logged against the wrong record" />
          </Field>
        </>
      )}
    </Modal>
  );
}

/**
 * Edit an existing activity — `ActivityDetail`'s "Edit" button. Shared by
 * `ActivitiesPage.jsx` and `CalendarPage.jsx` so a reschedule looks and
 * behaves the same whichever screen it's opened from. PATCHes the fields a
 * person is actually likely to correct — kind, subject, notes, priority,
 * schedule — with `row_version` for optimistic locking; "related to" is
 * shown but not changeable here, since re-anchoring which record an activity
 * belongs to is a bigger, riskier edit nobody has asked for.
 *
 * `scheduled_start`, if set, must be now or later: this form edits work still
 * to be done, not a record of the past, so backdating it would silently turn
 * an open task into one that was always "overdue" by design rather than by
 * neglect. Enforced both as the input's `min` and again on save, since a
 * `datetime-local` field only clamps the date part in some browsers.
 */
export function EditActivityModal({ activity, onClose, onSaved }) {
  const update = useUpdateActivity();
  const [kind, setKind] = useState('task');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [priority, setPriority] = useState('Normal');
  const [scheduledAt, setScheduledAt] = useState('');
  const [dateError, setDateError] = useState('');

  useEffect(() => {
    if (!activity) return;
    setKind(activity.kind);
    setSubject(activity.subject ?? '');
    setBody(activity.body ?? '');
    setPriority(activity.priority ?? 'Normal');
    setScheduledAt(activity.scheduled_start ? toLocalInputValue(activity.scheduled_start) : '');
    setDateError('');
  }, [activity]);

  const related = activity ? relatedRecordLinks(activity.related) : [];
  const nowLocal = useMemo(() => toLocalInputValue(new Date()), []);

  const validateSchedule = (value) => {
    if (value && new Date(value) < new Date()) {
      setDateError('Must be now or later.');
      return false;
    }
    setDateError('');
    return true;
  };

  const canSave = Boolean(subject.trim()) && !dateError;

  const save = async () => {
    if (!subject.trim() || !validateSchedule(scheduledAt)) return;
    const patch = {
      kind,
      subject: subject.trim(),
      priority,
      scheduled_start: scheduledAt ? new Date(scheduledAt).toISOString() : null,
    };
    /* `body` is optional on PATCH but not nullable — unlike scheduled_start,
       there's no way to clear notes once set through this endpoint, so an
       emptied textarea just leaves the existing value alone rather than
       sending a `null` the API rejects with 422. */
    if (body.trim()) patch.body = body.trim();
    await update.mutateAsync({ id: activity.id, patch, rowVersion: activity.row_version });
    onSaved();
  };

  return (
    <Modal
      open={Boolean(activity)}
      onClose={onClose}
      title="Edit activity"
      footer={(
        <>
          <button type="button" className="btn btn-cancel btn-sm-x" onClick={onClose}>Cancel</button>
          <button type="button" className="btn btn-save btn-sm-x" onClick={save} disabled={!canSave || update.isPending}>
            {update.isPending ? <><ButtonSpinner />Saving…</> : 'Save'}
          </button>
        </>
      )}
    >
      {activity && (
        <>
          <Field label="Kind">
            <select className="form-select" value={kind} onChange={(e) => setKind(e.target.value)}>
              {KIND_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </Field>
          <Field label="Subject" required>
            <input className="form-control" value={subject} onChange={(e) => setSubject(e.target.value)} autoFocus />
          </Field>
          {related.length > 0 && (
            <Field label="Related to" help="Not editable here.">
              <div className="t-sm ink-2">{related.map((r) => r.text).join(', ')}</div>
            </Field>
          )}
          <Field label="Notes">
            <textarea className="form-control" rows={3} value={body} onChange={(e) => setBody(e.target.value)} />
          </Field>
          <div className="row g-2">
            <div className="col-6">
              <Field label="Priority">
                <select className="form-select" value={priority} onChange={(e) => setPriority(e.target.value)}>
                  {PRIORITY_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </Field>
            </div>
            <div className="col-6">
              <Field
                label="Scheduled for"
                help={!dateError ? 'Optional — leave blank for a plain log entry.' : undefined}
                error={dateError}
              >
                <input
                  type="datetime-local"
                  className="form-control"
                  min={nowLocal}
                  value={scheduledAt}
                  onChange={(e) => { setScheduledAt(e.target.value); validateSchedule(e.target.value); }}
                />
              </Field>
            </div>
          </div>
        </>
      )}
    </Modal>
  );
}
